import { useEffect } from "react";
import { getEnvironment } from "../utils/getEnvironment";

declare global {
    interface Window {
        Weglot?: {
            initialize: (config: {
                api_key: string;
                auto_switch: boolean;
                wait_transition?: boolean;
                extra_definitions?: Array<{
                    type: number;
                    selector: string;
                    attribute: string;
                }>;
            }) => void;
            website: string;

            options: {
                languages: Array<{
                    language_from: string; // Original language code
                    language_to: string; // Target language code
                }>;
                language_from: string; // Original language code
            };
            getCurrentLang: () => string; // Method to get the current language
            getLanguageName: (lang: string) => string; // Method to get the language name based on the language code
            switchTo: (lang: string) => void; // Method to switch the language
            on: (event: string, callback: (lang: string) => void) => void; // Event listener for language changes
            getBestAvailableLanguage: () => String;
        };
        __weglotReady?: boolean;
    }
}

const environment = getEnvironment();

// Languages a landing can hand off. Keep in sync with landing-factory locales.
const INHERITABLE_LANGS = ["en", "ru"];

// Read ?lang=… from the entry URL (set by the landing page / referral redirect)
// and switch Weglot to it, so the creation flow continues in the landing's
// language instead of the default EN. No-op when absent, unsupported, or already
// active. Weglot then persists the choice through the rest of the flow.
const applyInheritedLang = () => {
    const wg = window.Weglot;
    if (!wg?.switchTo) return;
    const lang = new URLSearchParams(window.location.search).get("lang");
    if (!lang || !INHERITABLE_LANGS.includes(lang)) return;
    if (wg.getCurrentLang?.() !== lang) wg.switchTo(lang);
};

const useWeglot = () => {
    useEffect(() => {
        if (!window.Weglot) return;

        // Avoid double-initialize
        if (!window.__weglotReady) {
            window.Weglot.initialize({
                api_key: environment.weglot_api_key,
                auto_switch: false,
                wait_transition: true,
            });

            // Mark ready when Weglot fires initialized
            window.Weglot.on?.("initialized", () => {
                window.__weglotReady = true;
                applyInheritedLang();
            });
        } else {
            // Already initialized (e.g. remount) — apply immediately.
            applyInheritedLang();
        }
    }, []);
};

export default useWeglot;
